import {
  AlertCircle,
  ArrowRight,
  Building2,
  CheckCircle2,
  Globe,
  Mail,
  MapPin,
  Phone,
  Sparkles,
  UserRound,
} from "lucide-react";
import { useState, type ComponentType } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, organizerApi } from "../../api/client";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";

const VALUE_PROPS = [
  {
    icon: CheckCircle2,
    title: "Sell across many channels",
    body: "Your screening lives on the public marketplace, your own embed, and shareable links.",
  },
  {
    icon: CheckCircle2,
    title: "Track sales and attendees",
    body: "Live dashboard for sales velocity, tier inventory, orders, and check-ins.",
  },
  {
    icon: CheckCircle2,
    title: "Gate-ready QR scanning",
    body: "Validate every ticket at entry from the same admin app — no extra hardware.",
  },
  {
    icon: CheckCircle2,
    title: "Payouts you can rely on",
    body: "Mobile money, card, and bank settlement. Track every payment by screening.",
  },
];

export default function OrganizerSignupPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    businessName: "",
    city: "",
    website: "",
    description: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (submitEvent: React.FormEvent<HTMLFormElement>) => {
    submitEvent.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await organizerApi.apply({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        businessName: form.businessName.trim(),
        city: form.city.trim() || undefined,
        website: form.website.trim() || undefined,
        description: form.description.trim() || undefined,
        password: form.password,
      });
      navigate(`/exhibitor/status?email=${encodeURIComponent(form.email.trim().toLowerCase())}`, {
        replace: true,
      });
    } catch (requestError) {
      const message =
        requestError instanceof ApiError
          ? requestError.message
          : requestError instanceof Error
            ? requestError.message
            : "Could not submit application";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-muted">
      <Navbar />

      <section className="relative overflow-hidden pb-20 pt-28 sm:pt-32 lg:pt-36">
        <div className="pointer-events-none absolute inset-x-0 top-20 -z-0 mx-auto h-72 w-[90%] max-w-5xl rounded-[60px] bg-gradient-to-br from-[var(--pp-gold)]/15 via-[#ffd07a]/10 to-transparent blur-3xl" />

        <div className="section-frame relative">
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
            <div className="order-2 lg:order-1">
              <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9a3412] dark:text-[#fdba74] shadow-sm">
                <Sparkles size={12} />
                Become an exhibitor
              </span>
              <h1 className="mt-5 text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl">
                Sell tickets, fill seats, get paid.
              </h1>
              <p className="mt-4 max-w-lg text-base leading-7 text-muted-foreground">
                Set up your exhibitor account in minutes. Our team reviews every application —
                once approved, you can publish screenings and start collecting payments straight away.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-1">
                {VALUE_PROPS.map((point) => {
                  const Icon = point.icon;
                  return (
                    <div
                      key={point.title}
                      className="flex items-start gap-3 rounded-2xl border border-line bg-white/80 dark:bg-white/5 p-4 shadow-[0_8px_24px_-16px_rgba(11,18,32,0.16)] backdrop-blur"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]">
                        <Icon size={16} />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-foreground">{point.title}</div>
                        <p className="mt-0.5 text-xs text-muted-foreground">{point.body}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="mt-6 text-xs text-muted-foreground">
                Already onboarded?{" "}
                <Link to="/admin/login" className="font-semibold text-[#c2410c] dark:text-[#fbbf24] hover:underline">
                  Sign in to your exhibitor dashboard
                </Link>
              </p>
            </div>

            <div className="order-1 lg:order-2">
              <div className="overflow-hidden rounded-3xl border border-line bg-surface shadow-[0_24px_60px_-24px_rgba(11,18,32,0.18)]">
                <div className="border-b border-line bg-surface-muted px-6 py-4">
                  <div className="text-sm font-bold text-foreground">Get started</div>
                  <div className="text-xs text-muted-foreground">
                    Tell us about your organization. We'll respond by email after review.
                  </div>
                </div>

                <form className="space-y-4 p-6 sm:p-7" onSubmit={handleSubmit}>
                  <Field
                    label="Your full name"
                    icon={UserRound}
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={(value) => setForm((current) => ({ ...current, name: value }))}
                    required
                  />
                  <Field
                    label="Business / exhibitor name"
                    icon={Building2}
                    type="text"
                    autoComplete="organization"
                    placeholder="e.g. Sunset Sounds Ltd."
                    value={form.businessName}
                    onChange={(value) =>
                      setForm((current) => ({ ...current, businessName: value }))
                    }
                    required
                  />
                  <Field
                    label="Work email"
                    icon={Mail}
                    type="email"
                    autoComplete="email"
                    placeholder="you@yourbrand.com"
                    value={form.email}
                    onChange={(value) => setForm((current) => ({ ...current, email: value }))}
                    required
                  />
                  <Field
                    label="Phone number"
                    icon={Phone}
                    type="tel"
                    autoComplete="tel"
                    placeholder="0244 000 000"
                    value={form.phone}
                    onChange={(value) => setForm((current) => ({ ...current, phone: value }))}
                    required
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      label="City"
                      icon={MapPin}
                      type="text"
                      value={form.city}
                      onChange={(value) => setForm((current) => ({ ...current, city: value }))}
                      placeholder="Accra"
                    />
                    <Field
                      label="Website (optional)"
                      icon={Globe}
                      type="url"
                      value={form.website}
                      onChange={(value) => setForm((current) => ({ ...current, website: value }))}
                      placeholder="https://"
                    />
                  </div>

                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-foreground">
                      Tell us about your screenings
                    </span>
                    <textarea
                      rows={3}
                      placeholder="What kinds of screenings do you run? Frequency, typical audience size, etc."
                      value={form.description}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, description: event.target.value }))
                      }
                      className="w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-[var(--pp-gold)] focus:ring-4 focus:ring-[rgba(245,179,3,0.18)]"
                    />
                  </label>

                  <div>
                    <label className="mb-1.5 flex items-center justify-between text-sm font-semibold text-foreground">
                      <span>Create a password</span>
                    </label>
                    <input
                      type="password"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      placeholder="At least 8 characters"
                      value={form.password}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, password: event.target.value }))
                      }
                      className="w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-[var(--pp-gold)] focus:ring-4 focus:ring-[rgba(245,179,3,0.18)]"
                    />
                    <p className="mt-1 text-[11px] text-[#9aa3b2]">
                      You'll use this to sign in once your application is approved.
                    </p>
                  </div>

                  {error && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-[rgba(220,38,38,0.18)] bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] px-3 py-2.5 text-sm text-[#b91c1c]">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span className="font-semibold">{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--pp-gold)] px-5 py-3 text-base font-bold text-foreground shadow-[0_8px_24px_-10px_rgba(245,179,3,0.55)] transition hover:bg-[#e2a503] active:bg-[#c79300] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? "Submitting..." : "Submit application"}
                    {!isSubmitting && <ArrowRight size={16} />}
                  </button>
                </form>

                <div className="border-t border-line bg-surface-muted px-6 py-4 text-center text-[11px] text-[#9aa3b2]">
                  We review applications within 1–2 business days. You'll get an email at the address
                  above when a decision is made.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Field({
  label,
  icon: Icon,
  type,
  value,
  onChange,
  required,
  placeholder,
  autoComplete,
}: {
  label: string;
  icon: ComponentType<{ size?: number; className?: string }>;
  type: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-foreground">{label}</span>
      <div className="flex items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2.5 transition focus-within:border-[var(--pp-gold)] focus-within:ring-4 focus-within:ring-[rgba(245,179,3,0.18)]">
        <Icon size={16} className="shrink-0 text-[#9aa3b2]" />
        <input
          type={type}
          required={required}
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-[#9aa3b2]"
        />
      </div>
    </label>
  );
}
